<script lang="ts" setup>
import debounce from 'debounce'

import type { DragData } from '@/composables/useDragDrop.ts'
import PlaceableItem from '@/components/visual-signature/PlaceableItem.vue'
import VisualSignatureSkeleton from '@/components/visual-signature/VisualSignatureSkeleton.vue'

const props = defineProps<{
  width: number
  height: number
  parameters: Partial<VisualSignatureData>
  signatureId?: string // The ID of the predefined visual signature entity
  positionId?: PositionId // The position ID of individual visual signature placements
  placementId?: string // The placement ID of the original visual signature entry
  owned?: boolean
  placeholder?: boolean // a.k.a. pre-placed
  draggable?: boolean
  removable?: boolean
  clustered?: boolean
  disabled?: boolean
}>()

const emit = defineEmits<{
  remove: []
  zoom: [element: HTMLElement]
}>()

const visualSignatureStore = useVisualSignatureStore()

const imageElement = useTemplateRef('imageElement')
provide(VS_IMAGE, imageElement)

const imageDimensions = reactive({
  width: visualSignatureStore.defaultSignatureDimensions.width,
  height: visualSignatureStore.defaultSignatureDimensions.height,
})

const invitingBusinessId = inject(INVITING_BUSINESS_ID, ref(''))
const { data: publicBusinessInfo } = usePublicInformation(invitingBusinessId, {
  watch: [invitingBusinessId],
})

const { isStarterPlan } = storeToRefs(useBusinessStore())

const logoOverridePattern = /(TEAM|PRO|SCALE)(_V\d+)?$/

const computedParams = computed(() => {
  const defaults: Partial<VisualSignatureData> = {}
  if (isStarterPlan.value) {
    // Starter plan users get more limited options by default
    Object.assign(defaults, { displayClaim: true, displayStandards: true, displayDate: true })
    if (!logoOverridePattern.test(publicBusinessInfo.value?.plan || '')) {
      // Starter plan users should see the Skribble logo by default, unless invited by a user with a high plan
      defaults.logo = 'skribble'
    }
  }

  return {
    ...defaults,
    ...props.parameters,
  }
})

const dragData = computed<DragData>(() => ({
  itemType: 'signature',
  positionId: props.positionId,
  placementId: props.placementId,
  author: computedParams.value.name,
  signerGroup: computedParams.value.signerGroup,
}))

const isLoading = ref(true)

const srcUrl = ref('')

const alt = computed(() => {
  return `${computedParams.value.name} signature image`
})

const onImgLoaded = () => {
  if (srcUrl.value) {
    URL.revokeObjectURL(srcUrl.value)
  }
}

const getCustomImage = async () => {
  if (props.signatureId) {
    return await visualSignatureStore.getPredefinedVisualSignature(props.signatureId, computedParams.value)
  } else if (computedParams.value.image) {
    return await visualSignatureStore.getVisualSignature(computedParams.value, computedParams.value.image)
  } else {
    return await visualSignatureStore.getVisualSignature(computedParams.value)
  }
}

const updateImage = debounce(async () => {
  isLoading.value = true
  const blob = props.owned
    ? await getCustomImage()
    : await visualSignatureStore.getVisualSignaturePreview(computedParams.value, computedParams.value.image)
  srcUrl.value = URL.createObjectURL(blob)
  isLoading.value = false
}, 300)

watch(
  computedParams,
  (newParams, oldParams) => {
    if (Object.keys(newParams).some(key => newParams[key] !== oldParams[key])) {
      void updateImage()
    }
  },
  { deep: true }
)

onMounted(() => {
  void updateImage()
  updateImage.flush()
})

defineExpose({
  updateImage,
})
</script>

<template>
  <placeable-item
    class="signature-item"
    data-cy="signature_image"
    :width="imageDimensions.width"
    :height="imageDimensions.height"
    :position-id="positionId"
    :drag-data="dragData"
    :loading="isLoading"
    :placeholder="placeholder"
    :draggable="draggable"
    :removable="removable"
    :disabled="disabled"
    :clustered="clustered"
    @zoom="emit('zoom', $event)"
    @remove="emit('remove')"
  >
    <v-fade-transition mode="out-in" :duration="150">
      <visual-signature-skeleton v-if="isLoading" />
      <img v-else ref="imageElement" :src="srcUrl" :alt="alt" @load="onImgLoaded" />
    </v-fade-transition>
  </placeable-item>
</template>

<style lang="scss" scoped>
.signature-item {
  aspect-ratio: 300 / 140;
}
</style>
